import React, { useEffect, useMemo } from "react";
import { Route, RouteProps, Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "./store";
import { changeLanguage } from "./i18n/i18n";
import MenuService from "./services/MenuService";
import NotFoundPage from "./components/shared/TailwindComponents/NotFoundPage";

interface PrivateRouteProps extends RouteProps {
  component?: React.ComponentType<any>;
  render?: () => React.ReactNode;
  subscriptionId?: string;
  restricted?: boolean;
  allowedUserTypes?: string[] | boolean; // Deprecated: kept for backward compatibility
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  component: Component,
  render: Render,
  subscriptionId,
  restricted = false,
  allowedUserTypes, // Deprecated
  ...rest
}) => {
  const prefs = useSelector((s: RootState) => s.lists.preferences);
  const userInfo = useSelector((s: RootState) => s.user.userInfo);
  const menuStructure = useSelector((s: RootState) => s.menu?.menuStructure);
  const location = useLocation();

  useEffect(() => {
    if (prefs?.Language) changeLanguage(prefs.Language);
  }, [prefs]);

  // Check route access based on Redux-cached menu structure (no API calls)
  const hasMenuAccess = useMemo(() => {
    // Special case: reset-password routes are always allowed
    if (location.pathname.startsWith("/reset-password/")) {
      return true;
    }

    // Root path "/" is always allowed for navigation redirects
    if (location.pathname === "/") {
      return true;
    }

    // Admin routes are accessible only to org admins
    if (location.pathname.startsWith("/admin")) {
      return userInfo?.is_org_admin === true;
    }

    // If menu is not yet loaded from Redux/persistence, deny access
    // (This will allow page to load with cached menu after page refresh)
    if (!menuStructure) {
      return false;
    }

    try {
      const accessibleRoutes = MenuService.extractAccessibleRoutes(menuStructure);

      // Route aliases: some features live under a different URL prefix
      // but share access with their parent menu item.
      const ROUTE_ALIASES: Record<string, string> = {
        '/playbooks': '/reviews',
      };

      let pathToCheck = location.pathname;
      for (const [alias, parent] of Object.entries(ROUTE_ALIASES)) {
        if (location.pathname.startsWith(alias)) {
          pathToCheck = parent + location.pathname.slice(alias.length);
          break;
        }
      }

      const hasAccess = MenuService.hasAccessToRoute(pathToCheck, accessibleRoutes);

      if (!hasAccess) {
        return false;
      }

      // Check if this route is admin-only and user is not admin
      const isAdminRoute = MenuService.isRouteAdminOnly(location.pathname, menuStructure);
      if (isAdminRoute && !userInfo?.is_org_admin) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('[PrivateRoute] Error checking menu access:', error);
      return false;
    }
  }, [location.pathname, userInfo?.is_org_admin, menuStructure]);

  // Reset-password routes bypass menu check
  if (typeof rest.path === "string" && rest.path.startsWith("/reset-password/")) {
    return <Route {...rest} render={(p) => (Component ? <Component {...p} /> : Render ? Render() : null)} />;
  }

  // Check subscription restriction
  if (!subscriptionId && restricted) return <Redirect to="/plans" />;

  // Menu-based route access check - show 404 if no access
  if (!hasMenuAccess) {
    return <Route {...rest} render={() => <NotFoundPage />} />;
  }

  // Handle deprecated allowedUserTypes (for backward compatibility during migration)
  if (typeof allowedUserTypes === "boolean" && !allowedUserTypes) {
    return <Route {...rest} render={() => <NotFoundPage />} />;
  }

  const renderInner = (props: any) => (Component ? <Component {...props} /> : Render ? Render() : null);
  return <Route {...rest} render={renderInner} />;
};

export default PrivateRoute;
